import React, { useState, useEffect } from 'react';
import './MemeBotz.css';

const MEMES_API = process.env.REACT_APP_API_URL || 'https://api.cinebotz.com';
const FB_POST_WIDTH = 500;

function normalizeMeme(raw) {
  return {
    photoId: raw.photoId || raw.id || '',
    photoUrl: raw.photoUrl || raw.url || '',
  };
}

function memeEmbedSrc(photoUrl) {
  const params = new URLSearchParams({
    href: photoUrl,
    show_text: 'false',
    width: String(FB_POST_WIDTH),
  });
  return `https://www.facebook.com/plugins/post.php?${params.toString()}`;
}

async function fetchMemeList(apiBase) {
  const base = apiBase.replace(/\/$/, '');
  const endpoints = ['memephotos-s3-v2', 'memephotos-v2'];

  for (const endpoint of endpoints) {
    const url = base ? `${base}/api/${endpoint}` : `/api/${endpoint}`;
    const res = await fetch(url);
    const contentType = res.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      if (!res.ok) continue;
      throw new Error('Server did not return JSON.');
    }
    if (!res.ok) continue;

    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.memes || data.items || []);
    if (list.length > 0) {
      return list
        .slice(0, 10)
        .map(normalizeMeme)
        .filter((meme) => meme.photoUrl);
    }
  }

  throw new Error('Memes API error. Make sure the backend is running on port 8081.');
}

function MemePhoto({ meme }) {
  return (
    <div className="meme-botz-post">
      <iframe
        src={memeEmbedSrc(meme.photoUrl)}
        title="Facebook meme"
        className="meme-botz-iframe"
        scrolling="no"
        frameBorder="0"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        loading="lazy"
      />
      <a
        href={meme.photoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="meme-botz-hit"
        aria-label="Open meme on Facebook"
      />
    </div>
  );
}

export function MemeBotz() {
  const [memes, setMemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchMemeList(MEMES_API)
      .then((list) => {
        if (cancelled) return;
        setMemes(list);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Could not load memes.');
          setMemes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="meme-botz meme-botz--loading">
        <div className="meme-botz-spinner" aria-hidden />
        <p className="meme-botz-loading-text">Loading Meme Botz…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="meme-botz meme-botz--error">
        <p className="meme-botz-error-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="meme-botz">
      <ul className="meme-botz-list">
        {memes.map((meme) => (
          <li key={meme.photoId || meme.photoUrl} className="meme-botz-item">
            <MemePhoto meme={meme} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default MemeBotz;
